import React, { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { trackEvent } from '../services/analytics.js';
import { LINKS, ICONS, FEATURE_META, STAT_META } from '../constants.js';
import { Sym, Svg, Button, ModalHeader, ModeIcon } from './ui.jsx';
import { DownloadButtons } from './DownloadButtons.jsx';

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

export const Stats = () => {
  const { t } = useLanguage();
  // Re-rolled on mount and on language change (t identity changes with locale)
  const items = useMemo(() => STAT_META.map((m) => ({ icon: m.icon, label: t.stats[m.key], value: pick(t.stats[m.values]) })), [t]);
  return (
    <section className="stats">
      <div className="stats-grid">
        {items.map((s) => (
          <div key={s.label} className="card stat">
            <span className="stat-icon"><Sym name={s.icon} fill /></span>
            <div style={{ minWidth: 0 }}>
              <div className="eyebrow stat-label">{s.label}</div>
              <div className="stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const Features = () => {
  const { t, language } = useLanguage();
  return (
    <section className="section">
      <div className="wrap">
        <div className="section-head">
          <div className="chip">{language === 'it' ? 'Modalità di gioco' : 'Game modes'}</div>
          <h2 className="h2">{t.features.title}</h2>
        </div>
        <div className="features-grid">
          {t.features.items.map((f) => {
            const meta = FEATURE_META[f.id] || FEATURE_META.classic;
            return (
              <div key={f.id} className="card lift feature">
                <ModalHeader tone={meta.tone} icon={meta.symbol} title={f.title} subtitle={f.description} />
                <div className="feature-foot">
                  <span className="eyebrow">{meta.mode}</span>
                  <ModeIcon mode={meta.mode} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Video = () => {
  const { t } = useLanguage();
  return (
    <section className="section">
      <div className="wrap" style={{ maxWidth: 1024 }}>
        <div className="video-head"><Sym name="movie" size={28} style={{ color: '#FF3CAC' }} /><h2>{t.video.title}</h2></div>
        <a href={LINKS.youtube} target="_blank" rel="noopener noreferrer" className="player" aria-label="Gameplay trailer (YouTube)"
           onClick={() => trackEvent('video_play_click', { video_id: 'tTvc4e4tsi0', platform: 'youtube' })}>
          <img src={LINKS.thumb} alt="" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.src = LINKS.thumbFallback; }} />
          <div className="vig" />
          <div className="play-wrap"><div className="play"><Sym name="play_arrow" size={44} fill /></div></div>
        </a>
      </div>
    </section>
  );
};

export const Waifus = () => {
  const { t } = useLanguage();
  return (
    <section className="section" style={{ overflow: 'hidden' }}>
      <div className="wrap">
        <div className="section-head">
          <div className="chip secondary">Roster</div>
          <h2 className="h2">{t.waifus.title}</h2>
          <p className="hand" style={{ fontSize: 26, color: 'rgba(var(--text-rgb),.8)', maxWidth: 640 }}>{t.waifus.subtitle}</p>
        </div>
        <div className="waifu-grid">
          {t.waifus.characters.map((w) => (
            <div key={w.id} className="waifu" style={{ '--theme-glow': w.theme + '66' }} role="article" aria-label={'Character info for ' + w.name}
                 onClick={() => trackEvent('waifu_card_click', { waifu_name: w.name, is_locked: !!w.isLocked })}>
              <img src={w.imageUrl} alt={w.isLocked ? 'Character Locked - Play to unlock' : w.name + ' - ' + w.role} className={w.isLocked ? 'locked-img' : ''} loading="lazy" decoding="async" width="228" height="380" />
              <div className="shade" />
              {w.isLocked && <div className="lock-overlay"><div className="lock-circle"><Sym name="lock" size={30} /></div></div>}
              <div className="foot">
                <div className="eyebrow" style={{ color: w.theme }}>{w.role}</div>
                <div className="name">{w.isLocked ? '???' : w.name}</div>
                <div className="bar" style={{ background: 'linear-gradient(90deg, ' + w.theme + ', transparent)' }} />
                <p className="persona">{w.personality}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Social = () => {
  const { t } = useLanguage();
  const s = t.social || {};
  const click = (platform, action) => trackEvent('social_interaction', { platform, action });
  return (
    <section className="section">
      <div className="wrap" style={{ maxWidth: 1100 }}>
        <div className="section-head" style={{ marginBottom: 40 }}>
          <h2 className="h2 sm">{s.title || 'Join the Community'}</h2>
          <p className="hand" style={{ fontSize: 24, color: 'rgba(var(--text-rgb),.7)' }}>{s.subtitle || 'Follow us on social media for updates and contests!'}</p>
        </div>
        <div className="social-grid">
          <div className="card social">
            <div className="social-icon" style={{ background: 'rgba(24,119,242,.2)', color: '#1877F2' }}><Svg d={ICONS.facebook} size={28} /></div>
            <h3>Facebook</h3>
            <p>Like our page for official announcements and events.</p>
            <a className="full" href={LINKS.facebook} target="_blank" rel="noopener noreferrer" onClick={() => click('facebook', 'follow_button')}><Button variant="info" fullWidth>Like Page</Button></a>
          </div>
          <div className="card social">
            <div className="social-icon" style={{ background: 'rgba(var(--text-rgb),.12)', color: 'var(--text)' }}><Svg d={ICONS.x} size={26} /></div>
            <h3>X (Twitter)</h3>
            <p>Follow us for quick updates, memes, and community highlights.</p>
            <a className="full" href={LINKS.x} target="_blank" rel="noopener noreferrer" onClick={() => click('twitter', 'follow_button')}><Button variant="outline" fullWidth>Follow</Button></a>
          </div>
          <div className="card social discord">
            <div className="social-icon"><Svg d={ICONS.discord} size={28} /></div>
            <h3>Discord</h3>
            <p>{s.discordText || 'Enter the magical world of Waifu Briscola! Join the server for exclusive news, polls, and to chat directly with devs.'}</p>
            <a className="full" href={LINKS.discord} target="_blank" rel="noopener noreferrer" onClick={() => click('discord', 'join_button')}><Button variant="purple" fullWidth><Sym name="auto_awesome" size={18} />{s.discordButton || 'Join the Magic'}</Button></a>
          </div>
        </div>
      </div>
    </section>
  );
};

export const CTAStrip = () => {
  const { t, language } = useLanguage();
  return (
    <section className="cta">
      <div className="cta-panel">
        <div className="eyebrow"><Sym name="swords" size={16} />{language === 'it' ? 'Entra in partita' : 'Join the match'}</div>
        <h2 className="h2">{t.ctaStrip.title}</h2>
        <p className="hand sub">{t.ctaStrip.subtitle}</p>
        <DownloadButtons event="cta_download_click" section="footer_cta" center />
      </div>
    </section>
  );
};
